import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Callout,
  DirectionalHint,
  DefaultButton,
  Toggle,
  Stack,
  FontIcon,
} from '@fluentui/react'
import {
  useDashboardPrefs,
  CHART_TYPE_OPTIONS,
  type DashboardChartType,
} from '../../context/DashboardPrefsContext'
import { useDashboardTokens } from '../../theme/dashboardTokens'

/**
 * Lifecycle Command Center — on-dashboard chart controls (v2, Lane A / V6).
 *
 * The accessible quick-switcher that sits on the chart card: a popover chart-type
 * menu (ARIA radiogroup with roving tabindex + arrow selection), a `role=switch`
 * Values toggle (FluentUI `Toggle`), range pills (7/30/90), and the View-as-table
 * mode toggle. The Settings "Appearance" card (V2) remains the labelled canonical
 * selector; this is the fast in-context switch that reads/writes the same prefs.
 *
 * Chart type + Values are global prefs (via `useDashboardPrefs`); range and the
 * table mode are the chart's own view state, passed in so the parent
 * (`LifecycleChart`) stays the single owner of them.
 */

const RANGES = [7, 30, 90] as const

interface ChartControlsProps {
  days: number
  onDaysChange: (d: number) => void
  viewAsTable: boolean
  onToggleTable: () => void
}

export function ChartControls({ days, onDaysChange, viewAsTable, onToggleTable }: ChartControlsProps) {
  const { chartType, setChartType, showValues, setShowValues } = useDashboardPrefs()

  return (
    <Stack
      horizontal
      wrap
      verticalAlign="center"
      tokens={{ childrenGap: 8 }}
      role="toolbar"
      aria-label="Chart controls"
    >
      <ChartTypeMenu chartType={chartType} setChartType={setChartType} />

      <Toggle
        label="Values"
        inlineLabel
        checked={showValues}
        onChange={(_, checked) => setShowValues(!!checked)}
        styles={{ root: { marginBottom: 0 }, label: { fontSize: 13 } }}
        ariaLabel="Show values on chart"
      />

      <Stack horizontal tokens={{ childrenGap: 4 }} verticalAlign="center" role="group" aria-label="Time range">
        {RANGES.map((d) => (
          <DefaultButton
            key={d}
            text={`${d}d`}
            primary={d === days}
            aria-pressed={d === days}
            onClick={() => onDaysChange(d)}
            styles={{ root: { minWidth: 42, height: 30, padding: '0 10px' } }}
          />
        ))}
      </Stack>

      <DefaultButton
        text={viewAsTable ? 'View as chart' : 'View as table'}
        aria-pressed={viewAsTable}
        iconProps={{ iconName: viewAsTable ? 'BarChartVertical' : 'Table' }}
        onClick={onToggleTable}
        styles={{ root: { height: 30 } }}
      />
    </Stack>
  )
}

interface ChartTypeMenuProps {
  chartType: DashboardChartType
  setChartType: (t: DashboardChartType) => void
}

/**
 * Popover chart-type picker. The button opens a `Callout` whose body is an ARIA
 * `radiogroup`: arrow keys move (and live-apply) the selection, Home/End jump,
 * Enter/Space/click confirm and close, Escape closes. Roving tabindex keeps a
 * single tab stop; focus returns to the trigger on close.
 */
function ChartTypeMenu({ chartType, setChartType }: ChartTypeMenuProps) {
  const tokens = useDashboardTokens()
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLDivElement>(null)
  const optionRefs = useRef<Array<HTMLDivElement | null>>([])

  const selectedIndex = Math.max(0, CHART_TYPE_OPTIONS.findIndex((o) => o.key === chartType))
  const [focusIdx, setFocusIdx] = useState(selectedIndex)

  const current = CHART_TYPE_OPTIONS[selectedIndex]

  // On open, focus the checked option.
  useEffect(() => {
    if (open) {
      setFocusIdx(selectedIndex)
      const id = requestAnimationFrame(() => optionRefs.current[selectedIndex]?.focus())
      return () => cancelAnimationFrame(id)
    }
  }, [open, selectedIndex])

  const close = useCallback((returnFocus = true) => {
    setOpen(false)
    if (returnFocus) {
      requestAnimationFrame(() => triggerRef.current?.querySelector('button')?.focus())
    }
  }, [])

  const move = useCallback(
    (next: number) => {
      const len = CHART_TYPE_OPTIONS.length
      const idx = ((next % len) + len) % len
      setFocusIdx(idx)
      setChartType(CHART_TYPE_OPTIONS[idx].key) // live-apply, like a native radiogroup
      optionRefs.current[idx]?.focus()
    },
    [setChartType],
  )

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      switch (e.key) {
        case 'ArrowDown':
        case 'ArrowRight':
          e.preventDefault()
          move(focusIdx + 1)
          break
        case 'ArrowUp':
        case 'ArrowLeft':
          e.preventDefault()
          move(focusIdx - 1)
          break
        case 'Home':
          e.preventDefault()
          move(0)
          break
        case 'End':
          e.preventDefault()
          move(CHART_TYPE_OPTIONS.length - 1)
          break
        case 'Enter':
        case ' ':
          e.preventDefault()
          setChartType(CHART_TYPE_OPTIONS[focusIdx].key)
          close()
          break
        case 'Escape':
          e.preventDefault()
          close()
          break
        case 'Tab':
          close(false)
          break
        default:
          break
      }
    },
    [focusIdx, move, setChartType, close],
  )

  return (
    <div ref={triggerRef}>
      <DefaultButton
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
        iconProps={{ iconName: 'BarChartVerticalFilter' }}
        menuIconProps={{ iconName: 'ChevronDown' }}
        styles={{ root: { height: 30 }, label: { fontWeight: 400 } }}
      >
        {current.label}
      </DefaultButton>

      {open && (
        <Callout
          target={triggerRef.current}
          onDismiss={() => close(false)}
          directionalHint={DirectionalHint.bottomLeftEdge}
          isBeakVisible={false}
          gapSpace={4}
          setInitialFocus={false}
          styles={{
            root: { borderRadius: 8 },
            calloutMain: {
              background: tokens.elevated || undefined,
              border: `1px solid ${tokens.line}`,
              borderRadius: 8,
              padding: 6,
              minWidth: 260,
            },
          }}
        >
          <div role="radiogroup" aria-label="Chart type" onKeyDown={onKeyDown}>
            {CHART_TYPE_OPTIONS.map((opt, i) => {
              const checked = opt.key === chartType
              return (
                <div
                  key={opt.key}
                  ref={(el) => {
                    optionRefs.current[i] = el
                  }}
                  role="radio"
                  aria-checked={checked}
                  tabIndex={i === focusIdx ? 0 : -1}
                  onClick={() => {
                    setChartType(opt.key)
                    close()
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    padding: '8px 10px',
                    borderRadius: 6,
                    cursor: 'pointer',
                    background: checked ? tokens.card2 || 'transparent' : 'transparent',
                  }}
                >
                  <FontIcon
                    iconName={checked ? 'RadioBtnOn' : 'RadioBtnOff'}
                    style={{ color: checked ? tokens.focus : tokens.muted, fontSize: 16, marginTop: 1 }}
                  />
                  <span style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: tokens.text }}>{opt.label}</span>
                    <span style={{ fontSize: 12, color: tokens.slate }}>{opt.description}</span>
                  </span>
                </div>
              )
            })}
          </div>
        </Callout>
      )}
    </div>
  )
}

export default ChartControls
